/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import axios from 'axios';
import * as codec from 'purify-ts/Codec';
import * as compression from '@codegrade/compression';
import { parseBool } from './boolean';
/**
 * Wraps `axios.get()` for retrieving arraybuffer without parsing to JSON automatically.
 *
 * @param route The route from which to retrieve the array buffer.
 * @returns Either the Axios response, or an error occurring during the get operation .
 */
async function getArrayBufferWithAxios(route) {
    /*
     * Axios automatically uses JSON.parse on everything they get,
     * even if you specify another response type.
     * So if you are downloading a JSON file, you get an object instead of an
     * ArrayBuffer. Overriding the transformResponse function avoids that.
     */
    return await axios.get(route, { responseType: 'arraybuffer', transformResponse: x => x });
}
// https://stackoverflow.com/questions/13405129/javascript-create-and-save-file
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function downloadFile(data, filename, contentType) {
    const file = new Blob([data], { type: contentType });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 0);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function downloadMirrorFile(file) {
    window.open(file.url);
}
const SignedUrlCodec = codec.Codec.interface({ url: codec.string });
/**
 * Retrieves a File from S3 storage as an ArrayBuffer using Axios starting form either the API
 * route to retrieve the s3 signed link, or the signed link itself.
 *
 * @param route The API route to retrieve the S3 signed link or the the link itself.
 * @returns Either an error occurring during the operation or the ArrayBuffer with the file data.
 */
export async function downloadRawFile(route) {
    // Cypress overwrites the `fetch` function but their implementation is broken
    // for binary data. It does not define an `arrayBuffer` method on the
    // `Response` it returns. The response does, however, have a `blob` method,
    // but the data it contains is incorrect, leading to images and pdf files not
    // being rendered. Performing the requests with axios works fine, though.
    const response = await getArrayBufferWithAxios(route);
    const headers = response.headers;
    if (headers != null && parseBool(headers['x-cg-is-url'], false)) {
        /* As the data is an array buffer, we need to decode the data before parsing. */
        const parsedResponseData = JSON.parse(new TextDecoder().decode(response.data));
        const decoded = SignedUrlCodec.decode(parsedResponseData);
        if (!decoded.isRight()) {
            throw new Error(decoded.extract().toString());
        }
        const { data } = await getArrayBufferWithAxios(decoded.extract().url);
        return data;
    }
    return response.data;
}
/**
 * Downloads a file from an S3 signed link.
 *
 * @param signedS3link The signed S3 link to download the file from.
 * @returns The file as an `ArrayBuffer`, or an error if the get fails.
 */
export async function downloadFileFromS3(signedS3link) {
    const { data } = await getArrayBufferWithAxios(signedS3link);
    return data;
}
/**
 * Downloads a compressed file from an S3 signed link and decompresses it.
 *
 * @param signedS3link The signed S3 link to download the file from.
 * @returns The file as a `Uint8Array`, or an error if the get fails.
 */
export async function downloadCompressedFileFromS3(signedS3link) {
    const data = await downloadFileFromS3(signedS3link);
    const compressed = compression.CompressionResult.fromCompressedBinary(new Uint8Array(data)).unsafeCoerce();
    return await compressed.decompressPromise();
}
